import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import type { ProjectTicketType } from '@vd/types';
import { Badge, Button, ControlRow, FormField, Input, Select, Switch, useToast } from '@vd/ui';
import { TICKET_TYPE_GLYPHS } from '@vd/annotation-core';
import { api } from '../../../lib/api';
import { useBoardPrefs } from '../../../features/board/boardPrefs';
import { DestructiveConfirm } from '../../../features/modals/DestructiveConfirm';
import { MarkerPicker } from '../../../features/ticketTypes/MarkerPicker';
import styles from '../Settings.module.css';

/**
 * How this project differs from the workspace's ticket types (TOADO-410).
 *
 * A DELTA, not a list of its own. The company set is canonical and every type
 * keeps a company-level identity, which is what makes a ticket moving between
 * projects still resolve and what gives the cross-project views one thing to
 * filter on. See `apps/api/src/ticketTypes.ts` for the full reasoning.
 *
 * Two things this page has to say out loud, because a reader will assume the
 * opposite of both:
 *
 *   - Hiding a type does not untype tickets that already carry it. It stops being
 *     offered for new ones HERE.
 *   - A type added here joins the COMPANY set, hidden elsewhere by default. Said
 *     at the moment of adding rather than discovered later in company settings.
 *
 * And a third, now that a type can be deleted from here (Toado ticket t-ER8UEc):
 * a delete is of the WORKSPACE's type, so it is offered only for a type no ticket
 * uses anywhere. One that is used says how many, where they are, and where to
 * change them. Deleted tickets do not count as using it (TOADO-599): the delete
 * says they hold it, and takes it off them.
 */
interface TypeUsage { ticketCount: number; liveTicketCount: number; projectTicketCount: number; archivedTicketCount?: number }
const UNUSED: TypeUsage = { ticketCount: 0, liveTicketCount: 0, projectTicketCount: 0, archivedTicketCount: 0 };

const tickets = (count: number) => `${count} ticket${count === 1 ? '' : 's'}`;

export function ProjectTicketTypesTab({ projectId, companyId, isOwner, embedded = false }: {
  projectId: string; companyId: string; isOwner: boolean;
  /**
   * Shown inside another page's modal (the widget's visitor types, TOADO-578),
   * whose header already names it: the page heading is left out.
   */
  embedded?: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const patchBoardPrefs = useBoardPrefs((s) => s.patch);
  const [name, setName] = useState('');
  const [glyph, setGlyph] = useState(TICKET_TYPE_GLYPHS[0]!.value);
  const [explaining, setExplaining] = useState<string | null>(null);
  /** Where the explained type's tickets go if they are all moved at once. */
  const [moveTo, setMoveTo] = useState('');
  /** The type being deleted, and how many deleted tickets the confirmation said hold it. */
  const [confirming, setConfirming] = useState<{ type: ProjectTicketType; heldByDeleted: number } | null>(null);

  const types = useQuery({
    queryKey: ['ticketTypes', projectId],
    queryFn: () => api.listProjectTicketTypes(projectId).then((r) => r.items),
  });
  const usage = useQuery({
    queryKey: ['ticketTypeUsage', projectId],
    queryFn: () => api.projectTicketTypeUsage(projectId)
      .then((r) => new Map(r.items.map((item) => [item.typeId, item] as const))),
    enabled: isOwner,
    // Read again whenever the page opens: these counts decide what Delete offers,
    // and a ticket deleted or retyped a moment ago elsewhere changes them.
    refetchOnMount: 'always',
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['ticketTypes'] });
    void qc.invalidateQueries({ queryKey: ['ticketTypeUsage'] });
    // A local rename changes what every card in this project says.
    void qc.invalidateQueries({ queryKey: ['tickets'] });
  };

  const override = useMutation({
    mutationFn: ({ typeId, body }: { typeId: string; body: Parameters<typeof api.updateProjectTicketType>[2] }) =>
      api.updateProjectTicketType(projectId, typeId, body),
    onSuccess: invalidate,
    onError: (error: unknown) => toast.push({
      variant: 'danger',
      title: 'Could not save that change.',
      description: error instanceof Error ? error.message : undefined,
    }),
  });

  const add = useMutation({
    mutationFn: () => api.addProjectTicketType(projectId, { name: name.trim(), glyph }),
    onSuccess: (created) => {
      setName('');
      invalidate();
      toast.push({
        variant: 'success',
        title: `Added ${created.name}.`,
        description: 'It has joined the workspace set and is hidden on other projects until they turn it on.',
      });
    },
    onError: (error: unknown) => toast.push({
      variant: 'danger',
      title: 'Could not add that type.',
      description: error instanceof Error ? error.message : undefined,
    }),
  });

  /**
   * Sent with no disposition, so the server refuses it if a ticket has taken the
   * type since the count was read, rather than untyping that ticket unasked. Only
   * when the confirmation said deleted tickets hold it does it ask for it to be
   * taken off them, which the server still refuses if any ticket that is not
   * deleted has it by then.
   */
  const remove = useMutation({
    mutationFn: ({ type, heldByDeleted }: { type: ProjectTicketType; heldByDeleted: number }) =>
      api.deleteTicketType(companyId, type.id, heldByDeleted > 0 ? { clearDeleted: true } : {}),
    onSuccess: (_result, { type }) => {
      invalidate();
      toast.push({ variant: 'success', title: `Deleted ${type.name}.` });
    },
    onError: (error: unknown, { type }) => {
      invalidate();
      toast.push({
        variant: 'danger',
        title: `Could not delete ${type.name}.`,
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });

  /**
   * Every ticket with a custom type moved to another, and the type deleted, in
   * one step (Jason, 10 Oct). The server does both in one transaction, across
   * every project in the workspace and archived and deleted tickets too, so
   * nothing is left holding the type the delete then refuses on.
   */
  const reassign = useMutation({
    mutationFn: ({ type, to }: { type: ProjectTicketType; to: ProjectTicketType }) =>
      api.deleteTicketType(companyId, type.id, { reassignTo: to.id }),
    onSuccess: (result, { type, to }) => {
      setExplaining(null);
      setMoveTo('');
      invalidate();
      toast.push({
        variant: 'success',
        title: `Moved ${tickets(result.ticketCount)} to ${to.name} and deleted ${type.name}.`,
      });
    },
    onError: (error: unknown, { type }) => {
      invalidate();
      toast.push({
        variant: 'danger',
        title: `Could not move the tickets from ${type.name}.`,
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });

  const showOnBoard = (type: ProjectTicketType) => {
    patchBoardPrefs(projectId, { viewMode: 'table', typeIds: [type.id] });
    navigate(`/projects/${projectId}/board`);
  };

  const items = types.data ?? [];

  if (!isOwner) {
    return (
      <section className={styles.section}>
        <h2 className={styles.h2}>Ticket types</h2>
        <p className={styles.sub}>
          The kinds of thing this project tracks. Only an owner can change them.
        </p>
        <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 6 }}>
          {items.filter((type) => !type.hidden).map((type) => (
            <li key={type.id}>{type.name}</li>
          ))}
          {items.every((type) => type.hidden) && (
            <li style={{ color: 'var(--text-tertiary)' }}>None are offered on this project.</li>
          )}
        </ul>
      </section>
    );
  }

  return (
    <>
      <section className={styles.section}>
        {!embedded && <h2 className={styles.h2}>Ticket types</h2>}
        <p className={styles.sub}>
          These come from the workspace. Rename one to match what this project&apos;s team calls it, or
          turn off the ones it does not use. Turning one off only stops it being offered for new
          tickets here; tickets that already have it keep it. Bug, Improvement and New feature are
          built in: you can turn them off, but not rename or delete them.
        </p>

        {items.map((type) => {
          const counts = usage.data?.get(type.id) ?? UNUSED;
          // In use: held by a ticket that is not deleted. Deleted ones do not stop a delete.
          const used = counts.liveTicketCount > 0;
          const heldByDeleted = counts.ticketCount - counts.liveTicketCount;
          const archived = counts.archivedTicketCount ?? 0;
          const elsewhere = Math.max(0, counts.liveTicketCount - counts.projectTicketCount - archived);
          return (
            <div key={type.id} style={{ paddingBlock: 6 }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto minmax(0, 1fr) auto auto auto',
                  gap: 10,
                  alignItems: 'center',
                }}
              >
                <ControlRow>
                  <Switch
                    checked={!type.hidden}
                    onChange={(e) => override.mutate({ typeId: type.id, body: { hidden: !e.target.checked } })}
                    aria-label={`Offer ${type.name} on this project`}
                  />
                </ControlRow>
                {type.builtin ? (
                  <BuiltInName
                    type={type}
                    onUseWorkspaceName={() => override.mutate({ typeId: type.id, body: { name: null } })}
                  />
                ) : (
                <Input
                  defaultValue={type.name}
                  aria-label={`Name for ${type.name} on this project`}
                  onBlur={(e) => {
                    const next = e.target.value.trim();
                    const current = type.name;
                    if (next === current) return;
                    /**
                     * Emptying the field clears the local name rather than storing an
                     * empty one, so there is a way back to the workspace's own name
                     * without having to remember what it was.
                     */
                    override.mutate({ typeId: type.id, body: { name: next || null } });
                  }}
                />
                )}
                <span style={{ fontSize: 12, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>
                  {/*
                    The company's name shown alongside a local rename, so nobody looking
                    at this project can conclude the workspace says something it does not.
                  */}
                  {type.builtin ? <Badge variant="outline">Built in</Badge>
                    : type.companyName ? `workspace: ${type.companyName}` : 'workspace name'}
                </span>
                <span
                  data-testid={`type-count-${type.id}`}
                  style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap', minWidth: 64, textAlign: 'right' }}
                >
                  {usage.isLoading ? '' : used ? tickets(counts.liveTicketCount) : 'Not used'}
                </span>
                {type.builtin ? (
                  /**
                   * No Delete for a built-in type. A custom type reaches "show its
                   * tickets on the board" through Delete's explanation; a built-in
                   * one gets the same thing here, so it is not lost with the button.
                   */
                  counts.projectTicketCount > 0 ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Show the ${tickets(counts.projectTicketCount)} with ${type.name} on this board`}
                      onClick={() => showOnBoard(type)}
                    >
                      Show on board
                    </Button>
                  ) : <span />
                ) : (
                <span style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                {/* The way to the tickets that use it, as a built-in type has (TOADO-599). */}
                {counts.projectTicketCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Show the ${tickets(counts.projectTicketCount)} with ${type.name} on this board`}
                    onClick={() => showOnBoard(type)}
                  >
                    Show on board
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Trash2 size={13} />}
                  aria-label={`Delete ${type.name}`}
                  aria-expanded={used ? explaining === type.id : undefined}
                  // Not while the counts refresh either: a count from before
                  // tickets took this type opened the "nothing uses it"
                  // confirmation for a type in use (the server still refused it).
                  disabled={usage.isFetching || usage.isError || remove.isPending}
                  onClick={() => {
                    if (used) {
                      setExplaining(explaining === type.id ? null : type.id);
                      setMoveTo('');
                    } else {
                      setConfirming({ type, heldByDeleted });
                    }
                  }}
                >
                  Delete
                </Button>
                </span>
                )}
              </div>
              {!type.builtin && used && explaining === type.id && (
                <div
                  role="note"
                  style={{
                    marginTop: 8, padding: '10px 12px', borderRadius: 8, fontSize: 13, lineHeight: 1.5,
                    background: 'var(--surface-hover)', color: 'var(--text-secondary)', display: 'grid', gap: 8,
                  }}
                >
                  <span data-testid={`type-explain-${type.id}`}>
                    {/* Where they are, so the owner knows where to go (TOADO-599). */}
                    {tickets(counts.liveTicketCount)} {counts.liveTicketCount === 1 ? 'uses' : 'use'} {type.name}:{' '}
                    {[
                      counts.projectTicketCount > 0 ? `${counts.projectTicketCount} on this board` : null,
                      archived > 0 ? `${archived} archived` : null,
                      // Not on this board and not archived: drafts, or other projects.
                      elsewhere > 0 ? `${elsewhere} in drafts or on other projects` : null,
                    ].filter(Boolean).join(', ')}
                    . To delete it, give {counts.liveTicketCount === 1 ? 'that ticket' : 'them'} another type first.
                  </span>
                  {/* All of them at once: pick the type, and move them and delete this one. */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                    <Select
                      value={moveTo}
                      onChange={(e) => setMoveTo(e.target.value)}
                      aria-label={`Type to move the tickets with ${type.name} to`}
                      style={{ width: 'auto', minWidth: 200 }}
                    >
                      <option value="">{counts.ticketCount === 1 ? 'Move it to…' : 'Move them all to…'}</option>
                      {items.filter((other) => other.id !== type.id).map((other) => (
                        <option key={other.id} value={other.id}>
                          {other.name}{other.hidden ? ' (off on this project)' : ''}
                        </option>
                      ))}
                    </Select>
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={!moveTo || reassign.isPending}
                      onClick={() => {
                        const to = items.find((other) => other.id === moveTo);
                        if (to) reassign.mutate({ type, to });
                      }}
                    >
                      {reassign.isPending
                        ? 'Moving…'
                        : `${counts.ticketCount === 1 ? 'Move it' : 'Move them all'} and delete ${type.name}`}
                    </Button>
                  </div>
                  <span>
                    That moves every ticket with {type.name}, on every project and archived ones too.
                    {heldByDeleted > 0 && ' Deleted tickets with it move as well, so a restored one keeps a type.'}
                  </span>
                  {/* Or one at a time: all of this board's in one place. */}
                  {counts.projectTicketCount > 0 && (
                    <div>
                      <Button variant="secondary" size="sm" onClick={() => showOnBoard(type)}>
                        Show the {tickets(counts.projectTicketCount)} on this board to retype
                        {counts.projectTicketCount === 1 ? ' it' : ' them'} yourself
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {items.length === 0 && !types.isLoading && (
          <p style={{ color: 'var(--text-tertiary)' }}>
            This workspace has no ticket types yet. Add them in company settings.
          </p>
        )}
      </section>

      <section className={styles.section}>
        <h2 className={styles.h2}>Add a new ticket type</h2>
        <p className={styles.sub}>
          Anything you add joins the workspace&apos;s set so a ticket keeps its type when it moves
          between projects. It stays turned off on other projects until they turn it on.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 8, alignItems: 'end' }}>
          <FormField label="Name">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Tech debt" maxLength={40} />
          </FormField>
          <Button variant="primary" disabled={!name.trim() || add.isPending} onClick={() => add.mutate()}>
            {add.isPending ? 'Adding…' : 'Add'}
          </Button>
        </div>
        <div style={{ height: 12 }} />
        <MarkerPicker value={glyph} onChange={setGlyph} />
      </section>

      <DestructiveConfirm
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        onConfirm={() => { if (confirming) remove.mutate(confirming); }}
        title={confirming ? `Delete ${confirming.type.name}?` : 'Delete type?'}
        description={confirming && confirming.heldByDeleted > 0
          ? `No ticket uses it, but ${confirming.heldByDeleted} deleted ticket${confirming.heldByDeleted === 1 ? '' : 's'} still `
            + `${confirming.heldByDeleted === 1 ? 'holds' : 'hold'} it. Deleting the type takes it off `
            + `${confirming.heldByDeleted === 1 ? 'that one' : 'them'}, so a restored ticket comes back with no type. `
            + 'It leaves the workspace’s set, so no project can offer it any more.'
          : 'No ticket uses it. It leaves the workspace’s set, so no project can offer it any more.'}
        confirmLabel="Delete"
      />
    </>
  );
}

/**
 * A built-in type's name, which a project cannot change (TOADO-578).
 *
 * A local name set before that rule shipped is still shown, with the way back
 * to the workspace's own name, since clearing one stays allowed.
 */
function BuiltInName({ type, onUseWorkspaceName }: { type: ProjectTicketType; onUseWorkspaceName: () => void }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{type.name}</span>
      {type.companyName && (
        <Button variant="ghost" size="sm" onClick={onUseWorkspaceName}>
          Use &ldquo;{type.companyName}&rdquo;
        </Button>
      )}
    </span>
  );
}
